import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { Search, DollarSign, Plus, Minus, User, Clock, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * 🔒 WALLET MANAGEMENT - OWNER ONLY
 * 
 * SECURITY: This component can ONLY be accessed by users with role='owner'
 * - Button is hidden for non-owners
 * - Component shows "Access Denied" for non-owners
 * - Database RLS blocks non-owners from querying wallet data
 */

const AdminWalletManagement = () => {
  // 🔒 Get user profile for role checking
  const { userProfile } = useAuth();
  
  // React Hooks - MUST be called before any conditional returns
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  
  // Modal form state
  const [amount, setAmount] = useState('');
  const [transactionType, setTransactionType] = useState('credit');
  const [reason, setReason] = useState('admin_adjustment');
  const [description, setDescription] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Fetch all users with wallet balances
  useEffect(() => {
    if (userProfile?.role === 'owner') {
      fetchUsers();
    }
  }, [userProfile]);
  
  // 🔒 SECURITY CHECK: Block access if not owner
  if (!userProfile || userProfile.role !== 'owner') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-red-600" size={40} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">🔒 Access Denied</h2>
          <p className="text-gray-600 mb-4">
            Wallet Management is restricted to Owner accounts only.
          </p>
          <p className="text-sm text-gray-500 mb-2">
            Your role: <span className="font-semibold text-red-600">{userProfile?.role || 'Not logged in'}</span>
          </p>
          <p className="text-xs text-gray-400">
            Only users with role='owner' can manage customer wallets and financial data.
          </p>
        </div>
      </div>
    );
  }

  // Helper: fetch ALL rows from a table, bypassing Supabase's 1000-row default limit
  const fetchAllRows = async (table, selectColumns, orderColumn = null) => {
    const PAGE_SIZE = 1000;
    let allData = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      let query = supabase
        .from(table)
        .select(selectColumns)
        .range(from, from + PAGE_SIZE - 1);

      if (orderColumn) {
        query = query.order(orderColumn);
      }

      const { data, error } = await query;
      if (error) throw error;

      allData = allData.concat(data || []);
      hasMore = data && data.length === PAGE_SIZE;
      from += PAGE_SIZE;
    }

    return allData;
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Get ALL users (paginated to bypass 1000-row limit)
      const profiles = await fetchAllRows('user_profiles', 'id, name, email, phone', 'name');

      // Get ALL wallet transactions (paginated to bypass 1000-row limit)
      let walletData;
      try {
        walletData = await fetchAllRows('wallet_transactions', 'user_id, amount, transaction_type');
      } catch (walletError) {
        console.error('❌ Wallet transactions error:', walletError);
        throw walletError;
      }

      console.log('📊 Wallet transactions loaded:', walletData?.length || 0);

      // Calculate balances
      const balances = {};
      if (walletData && walletData.length > 0) {
        walletData.forEach(txn => {
          if (!balances[txn.user_id]) balances[txn.user_id] = 0;
          
          const amount = parseFloat(txn.amount);
          if (isNaN(amount)) {
            console.warn('⚠️ Invalid amount:', txn);
            return;
          }
          
          if (txn.transaction_type === 'credit') {
            balances[txn.user_id] += amount;
          } else if (txn.transaction_type === 'debit') {
            balances[txn.user_id] -= amount;
          }
        });
        
        console.log('💰 Calculated balances:', balances);
      } else {
        console.warn('⚠️ No wallet transactions found');
      }

      // Merge profiles with balances
      const usersWithBalances = profiles.map(profile => ({
        ...profile,
        balance: balances[profile.id] || 0
      }));

      setUsers(usersWithBalances);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTransactions = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setTransactions(data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  };

  const handleAddCredit = async () => {
    if (!selectedUser || !amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const amountNum = parseFloat(amount);
      const newBalance = transactionType === 'credit' 
        ? selectedUser.balance + amountNum
        : selectedUser.balance - amountNum;

      // Insert transaction
      const { error: txnError } = await supabase
        .from('wallet_transactions')
        .insert({
          user_id: selectedUser.id,
          transaction_type: transactionType,
          amount: amountNum,
          balance_after: newBalance,
          reason: reason,
          description: description || `Admin ${transactionType} - ${reason}`,
          status: 'approved'
        });

      if (txnError) throw txnError;

      setSuccess(`Successfully ${transactionType === 'credit' ? 'added' : 'deducted'} R${amountNum.toFixed(2)} ${transactionType === 'credit' ? 'to' : 'from'} ${selectedUser.name}'s wallet`);
      
      // Reset form
      setAmount('');
      setDescription('');
      
      // Refresh data
      await fetchUsers();
      await fetchUserTransactions(selectedUser.id);
      
      // Update selected user balance
      setSelectedUser({
        ...selectedUser,
        balance: newBalance
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowModal(false);
        setSuccess('');
      }, 2000);

    } catch (err) {
      console.error('Error processing transaction:', err);
      setError(`Failed to ${transactionType === 'credit' ? 'add credit' : 'deduct amount'}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user, type = 'credit') => {
    setSelectedUser(user);
    setTransactionType(type);
    setShowModal(true);
    setError('');
    setSuccess('');
    fetchUserTransactions(user.id);
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  const reasonOptions = [
    { value: 'admin_adjustment', label: 'Admin Adjustment' },
    { value: 'refund', label: 'Refund' },
    { value: 'compensation', label: 'Compensation' },
    { value: 'promotion', label: 'Promotion Credit' },
    { value: 'correction', label: 'Correction' },
    { value: 'bonus', label: 'Bonus Credit' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">💰 Wallet Management</h1>
              <p className="text-gray-600">Manage user wallet balances and transactions</p>
            </div>
            <div className="bg-green-100 px-4 py-2 rounded-lg">
              <p className="text-sm text-green-800 font-semibold">🔒 Owner Access</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Wallet Balance
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                            <User size={20} className="text-green-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                        <div className="text-sm text-gray-500">{user.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-lg font-bold ${user.balance > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          R{user.balance.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openModal(user, 'credit')}
                          className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 mr-2"
                        >
                          <Plus size={16} />
                          Add Credit
                        </button>
                        <button
                          onClick={() => openModal(user, 'debit')}
                          className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          <Minus size={16} />
                          Deduct
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {transactionType === 'credit' ? 'Add Credit' : 'Deduct Amount'} - {selectedUser.name}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Current Balance: <span className="font-bold text-green-600">R{selectedUser.balance.toFixed(2)}</span>
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Success/Error Messages */}
              {success && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="text-green-600" size={24} />
                  <p className="text-green-800 font-medium">{success}</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="text-red-600" size={24} />
                  <p className="text-red-800 font-medium">{error}</p>
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Amount (R) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {reasonOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add any notes about this transaction..."
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                />
              </div>

              {/* Preview */}
              {amount && parseFloat(amount) > 0 && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Preview:</strong> {selectedUser.name}'s balance will change from{' '}
                    <span className="font-bold">R{selectedUser.balance.toFixed(2)}</span> to{' '}
                    <span className={`font-bold ${transactionType === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      R{(transactionType === 'credit' 
                        ? selectedUser.balance + parseFloat(amount)
                        : selectedUser.balance - parseFloat(amount)
                      ).toFixed(2)}
                    </span>
                  </p>
                </div>
              )}

              {/* Recent Transactions */}
              {transactions.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock size={18} />
                    Recent Transactions
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {transactions.slice(0, 5).map(txn => (
                      <div key={txn.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {txn.transaction_type === 'credit' ? '+' : '-'}R{parseFloat(txn.amount).toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-600">{txn.description}</p>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(txn.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                disabled={loading}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredit}
                disabled={loading || !amount || parseFloat(amount) <= 0}
                className={`px-6 py-3 rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  transactionType === 'credit' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {loading ? 'Processing...' : transactionType === 'credit' ? 'Add Credit' : 'Deduct Amount'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWalletManagement;
